import { Component, ViewChild, OnInit } from '@angular/core';
import { Profile } from 'src/app/general-components/profile/profile';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { AuthService } from 'src/app/auth/auth.service';
import { Router } from '@angular/router';
import { map, switchMap } from 'rxjs/operators';
import { FeedbackComponent } from 'src/app/general-components/feedback/feedback.component';
import { SCSNCustomer } from './scsn-customer.model';
import { ScsnCustomersService } from './scsn-customers.service';
import { NewSCSNCustomer } from './new-scsn-customer.model';
import { CustomerInfo } from './customer-info.model';
import { ExtendedSCSNCustomer } from './extended-scsn-customer.model';
import { ScsnCustomersTableComponent } from './scsn-customers-table/scsn-customers-table.component';
import { ScsnCustomersFormComponent } from './scsn-customers-form/scsn-customers-form.component';

@Component({
  selector: 'app-scsn-customers',
  templateUrl: './scsn-customers.component.html',
  styleUrls: ['./scsn-customers.component.scss'],
})
export class ScsnCustomersComponent implements OnInit {
  @ViewChild(ScsnCustomersTableComponent)
  scsnCustomerTable!: ScsnCustomersTableComponent;
  @ViewChild(ScsnCustomersFormComponent)
  scsnCustomerForm!: ScsnCustomersFormComponent;
  @ViewChild(FeedbackComponent)
  feedbackComponent!: FeedbackComponent;

  extSCSNCustomers: ExtendedSCSNCustomer[] = [];

  profile!: Profile;

  deleteCandidateSCSNCustomer?: SCSNCustomer;
  isLoading: boolean = false;
  failedToLoad: boolean = false;

  page: number = 1;
  constructor(
    private scsnCustomerService: ScsnCustomersService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fetchAndCombineSCSNCustomerData();
    this.profile = this.authService.getUserInfo();
  }

  fetchAndCombineSCSNCustomerData(): void {
    this.isLoading = true;
    this.scsnCustomerService
      .getSCSNCustomers()
      .pipe(
        map(customers => {
          const customerNumbers = customers.map(customer => customer.indiCustomerNumber);
          return { customers, customerNumbers };
        }),
        switchMap(({ customers, customerNumbers }) =>
          this.scsnCustomerService
            .getBulkCustomerInfo(customerNumbers)
            .pipe(map(customerInfos => ({ customers, customerInfos })))
        ),
        map(({ customers, customerInfos }) => this.combineCustomerInfo(customers, customerInfos))
      )
      .subscribe({
        next: combinedData => {
          this.extSCSNCustomers = combinedData;
          this.extSCSNCustomers.sort((a, b) => {
            const timeA = a.scsnCustomer.updatedAt ? new Date(a.scsnCustomer.updatedAt).getTime() : 0;
            const timeB = b.scsnCustomer.updatedAt ? new Date(b.scsnCustomer.updatedAt).getTime() : 0;
            return timeB - timeA;
          });
          this.isLoading = false;
        },
        error: (error: Error) => {
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.error,
            'Er is iets fout gegaan bij het ophalen van de SCSN klanten',
            'Terug naar hoofdmenu',
            false
          );
          this.isLoading = false;
          this.failedToLoad = true;
          console.error(error);
        },
      });
  }

  combineCustomerInfo(customers: SCSNCustomer[], customerInfos: CustomerInfo[]): ExtendedSCSNCustomer[] {
    const customerInfoMap = new Map<string, CustomerInfo>();

    customerInfos.forEach(info => {
      customerInfoMap.set(info.customerNumber, info);
    });
    return customers.map(customer => {
      const customerInfo = customerInfoMap.get(customer.indiCustomerNumber);
      return { scsnCustomer: customer, customerInfo }; // this will return an ExtendedSCSNCustomer with undefined CustomerInfo, we don't really expect it to happen but it's possble
    });
  }

  createSCSNCustomer(extSCSNCustomer: ExtendedSCSNCustomer): void {
    const newSCSNCustomer = NewSCSNCustomer.fromSCSNCustomer(
      extSCSNCustomer.scsnCustomer, //
      this.profile.fullName, //
      ''
    );

    this.scsnCustomerService.createSCSNCustomer(newSCSNCustomer).subscribe({
      next: scsnCustomer => {
        const newExtSCSNCustomer: ExtendedSCSNCustomer = {
          scsnCustomer,
          customerInfo: extSCSNCustomer.customerInfo,
        };
        this.addToSCSNCustomers(newExtSCSNCustomer);
        if (extSCSNCustomer.customerInfo) {
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.success,
            `${extSCSNCustomer.customerInfo.companyName} is aangesloten!`,
            undefined,
            false
          );
        } else {
          this.feedbackComponent.showFeedback(
            '',
            FeedbackType.success,
            `Het bedrijf is aangesloten!`,
            undefined,
            false
          );
        }
        this.fadeOutFeedback();
      },
      error: (error: Error) => {
        this.feedbackComponent.showFeedback(
          '',
          FeedbackType.error,
          'Er is iets misgegaan bij het aansluiten van de SCSN Klant',
          undefined,
          false
        );
        this.fadeOutFeedback();
        console.error(error);
      },
    });
  }

  editSCSNCustomer(extSCSNCustomer: ExtendedSCSNCustomer): void {
    this.scsnCustomerForm.setEditSCSNCustomer(extSCSNCustomer);
  }

  updateSCSNCustomer(extSCSNCustomer: ExtendedSCSNCustomer): void {
    extSCSNCustomer.scsnCustomer.updatedBy = this.profile.fullName;
    const newSCSNCustomer = NewSCSNCustomer.fromSCSNCustomer(extSCSNCustomer.scsnCustomer, '', this.profile.fullName);

    this.scsnCustomerService.updateSCSNCustomer(newSCSNCustomer, extSCSNCustomer.scsnCustomer.id).subscribe({
      next: () => {
        this.scsnCustomerService.getSCSNCustomer(extSCSNCustomer.scsnCustomer.id).subscribe(scsnCustomer => {
          const updatedExtSCSNCustomer: ExtendedSCSNCustomer = {
            scsnCustomer,
            customerInfo: extSCSNCustomer.customerInfo,
          };
          this.updateSCSNCustomerInList(updatedExtSCSNCustomer);
          if (extSCSNCustomer.customerInfo) {
            this.feedbackComponent.showFeedback(
              '',
              FeedbackType.success,
              `${extSCSNCustomer.customerInfo.companyName} is aangepast!`,
              undefined,
              false
            );
          } else {
            this.feedbackComponent.showFeedback(
              '',
              FeedbackType.success,
              `Het bedrijf is aangepast!`,
              undefined,
              false
            );
          }
          this.fadeOutFeedback();
        });
      },
      error: (error: Error) => {
        this.feedbackComponent.showFeedback(
          '',
          FeedbackType.error,
          'Er is iets misgegaan bij het aanpassen van de SCSN Klant.',
          undefined,
          false
        );
        this.fadeOutFeedback();
        console.error(error);
      },
    });
  }

  updateSCSNCustomerInList(extSCSNCustomer: ExtendedSCSNCustomer): void {
    const foundExtSCSNCustomer: ExtendedSCSNCustomer | undefined = this.extSCSNCustomers.find(
      s => s.scsnCustomer.id == extSCSNCustomer.scsnCustomer.id
    );
    if (foundExtSCSNCustomer) {
      foundExtSCSNCustomer.scsnCustomer = extSCSNCustomer.scsnCustomer;
      foundExtSCSNCustomer.customerInfo = extSCSNCustomer.customerInfo;
    }
  }

  addToSCSNCustomers(extSCSNCustomer: ExtendedSCSNCustomer): void {
    this.extSCSNCustomers.push(extSCSNCustomer);
    this.scsnCustomerTable.sortByField('updatedAt', false);
  }

  returnHome(): void {
    void this.router.navigate(['home']);
  }

  fadeOutFeedback(): void {
    this.feedbackComponent.fadeOut(3000);
  }
}
